import { SearchableList } from '@platform/ui-library';
import { useState } from 'react';
import { AutocompleteControl } from '@app/form_components';
import {
  DesignIntent,
  DesignIntentConfig,
  Option
} from '@app/types';
import { Case, convertCase } from '@app/utilities';
import styles from './LinkIntentHierarchy.module.scss';

export enum HierarchyType {
  children = 'children',
  parents = 'parents'
}

export type LinkIntentHierarchyListProps = {
  disabled?: boolean;
  intentConfig: DesignIntentConfig;
  onConfigChange: <K extends keyof(DesignIntentConfig)>(
    changes: Pick<DesignIntentConfig, K>
  ) => void;
  onSearch: (inputValue: string) => Option<DesignIntent>[];
  type: HierarchyType;
};

export const LinkIntentHierarchyList = ({
  disabled,
  intentConfig,
  onConfigChange,
  onSearch,
  type
}: LinkIntentHierarchyListProps) => {
  const [search, setSearch] = useState('');
  const typeLabel = convertCase(type, Case.title);
  const hierarchyItems = intentConfig[type];

  const onAdd = (option: DesignIntent | null): void => {
    setSearch('');
    const {
      id,
      name,
      props
    } = option!;
    onConfigChange({
      [type as 'children']: [...hierarchyItems, {
        customId: id,
        description: props.config.description,
        name
      }]
    });
  };

  const onDelete = (idToDelete: string): void => {
    onConfigChange({
      [type as 'children']: hierarchyItems.filter(({ customId }) => {
        return customId !== idToDelete;
      })
    });
  };

  return (
    <div className={styles.column}>
      <AutocompleteControl<DesignIntent>
        getOptions={onSearch}
        labels={{
          isLoading: 'Looking for Design Intents...',
          notFound: 'The Design Intent you are looking for does not exist.'
        }}
        name="hierarchyIntent"
        onChange={onAdd}
        onSearchValueChange={setSearch}
        searchValue={search}
        value={null}
      />
      <SearchableList
        hasCount
        classes={{
          root: styles.listContainer,
          list: styles.list
        }}
        hasSearch={disabled}
        disabled={disabled}
        label={`Added ${typeLabel} Intents`}
        onDelete={onDelete}
        items={hierarchyItems.map((i) => {
          return {
            id: i.customId,
            value: i.name
          };
        })}
      />
    </div>
  );
};
